//@flow
import QuestionFactory from 'components/ZentakeForm/Questions/QuestionFactory'
import max from 'lodash/max'
import * as React from 'react'
import type { Answer } from 'types/answer'
import type { Question } from 'types/question'
import type { Response } from 'types/Response'
import type { SessionObject } from 'types/session-object'
import Tooltip from 'ui/atoms/Tooltip'
import Text from 'ui/Text'
import { hasText } from 'utils/helpers'
import cn from 'utils/tailwind/cn'

import { Section } from '@/ui/organisms/Section'

type Props = {
  question: Question,
  isQuestionReadonly: boolean,
  willShowNotAllowedCursor: boolean,
  answer: ?Answer,
  isInternalForm?: boolean,
  isQuestionDirty: boolean,
  isQuestionValid: boolean,
  questionError?: ?string,
  onAnswerChanged: (Question, string) => void,
  mode: void | 'client' | 'office' | 'read-only' | 'preview',
  questionIdToScroll: ?string,
  setQuestionIdToScroll: (?string) => void,
  setResponse: (?Response | ((?Response) => ?Response)) => void,
  visibleByConditionalRules: boolean,
  responseView: ?boolean,
  response: Response,
  sessions?: SessionObject,
  section: Section,
  page: Page,
}

const FormSectionQuestionRow: React.ComponentType<any> = React.memo(
  (props: Props): React.Node => {
    const {
      mode,
      question,
      questionIdToScroll,
      setQuestionIdToScroll,
      setResponse,
      visibleByConditionalRules,
      willShowNotAllowedCursor,
      responseView,
      isQuestionReadonly,
      answer,
      isQuestionDirty,
      isInternalForm,
      isQuestionValid,
      questionError,
      onAnswerChanged,
      response,
      sessions,
      page,
    } = props

    const questionRef = React.useRef()

    let isVisibleByConditionalRules = true
    if (!responseView) isVisibleByConditionalRules = visibleByConditionalRules

    let isHidden = false
    if (!responseView) isHidden = question?.hidden

    const questionId = question?.id

    React.useEffect(() => {
      if (questionId === questionIdToScroll) {
        window.scrollTo({
          left: 0,
          top: max([(questionRef?.current?.offsetTop || 0) - 32, 0]),
          behavior: 'smooth',
        })
        setQuestionIdToScroll(null)
      }
    }, [questionId, questionIdToScroll, setQuestionIdToScroll])

    React.useEffect(() => {
      if (!isVisibleByConditionalRules && setResponse) {
        // remove answers for this question, if they exist
        const hasAnswers = Boolean(answer)

        if (!hasAnswers) return

        setResponse((response) => {
          if (!response) return response

          return {
            ...response,
            answers: [...(response.answers || [])].filter(
              (a) => a.question !== question.id
            ),
          }
        })
      }
    }, [
      question,
      visibleByConditionalRules,
      setResponse,
      responseView,
      answer,
      isVisibleByConditionalRules,
    ])

    const qType =
      question?.question_type?.question_type || question?.question_type
    const hasHelperText = React.useCallback(
      () => qType !== 'matrix' && qType !== 'signature' && qType !== 'table',
      [qType]
    )

    const toolTipText = React.useMemo(() => {
      if (mode === 'office') return null

      if (question.is_office_use_only) return 'Office use only'

      if (
        question.is_read_only &&
        question?.question_type?.question_subtype !== 'handout'
      )
        return 'Read only'

      return null
    }, [question, mode])

    const styleDisplayColumns = {
      'sm:col-span-0': question.display_columns === 0,
      'sm:col-span-1': question.display_columns === 1,
      'sm:col-span-2': question.display_columns === 2,
      'sm:col-span-3': question.display_columns === 3,
      'sm:col-span-4': question.display_columns === 4,
      'sm:col-span-5': question.display_columns === 5,
      'sm:col-span-6': question.display_columns === 6,
      'sm:col-span-7': question.display_columns === 7,
      'sm:col-span-8': question.display_columns === 8,
      'sm:col-span-9': question.display_columns === 9,
      'sm:col-span-10': question.display_columns === 10,
      'sm:col-span-11': question.display_columns === 11,
      'sm:col-span-12': question.display_columns === 12,
    }

    if (!isVisibleByConditionalRules || isHidden) {
      return <i className={cn('col-span-12', styleDisplayColumns)} />
    }

    return (
      <div className={cn('col-span-12', styleDisplayColumns)} ref={questionRef}>
        <div className="mt-6">
          {(qType !== 'display_only' || hasText(question.text)) && (
            <Tooltip
              enabled={willShowNotAllowedCursor && !!toolTipText}
              label={toolTipText || ''}
            >
              <QuestionFactory
                answer={answer}
                isInternalForm={isInternalForm}
                isQuestionDirty={isQuestionDirty}
                isQuestionReadonly={isQuestionReadonly}
                isQuestionValid={isQuestionValid}
                mode={mode}
                onAnswerChanged={onAnswerChanged}
                page={page}
                question={question}
                questionError={questionError}
                response={response}
                sessions={sessions}
                willShowNotAllowedCursor={willShowNotAllowedCursor}
              />
            </Tooltip>
          )}
        </div>
        {hasHelperText() && (
          <Text.HelperText>{question.help_text}</Text.HelperText>
        )}
      </div>
    )
  }
)

export default FormSectionQuestionRow
